import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { calendarKeyboardDate } from './calendarKeyboard';

/** 달력 전체에 하나의 Tab 진입점을 두고 방향키로 날짜와 월을 탐색합니다. */
export function useCalendarKeyboard({ from, to, initialDate, onNavigate }: {
  from: string; to: string; initialDate: string; onNavigate: (date: string) => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [focusedDate, setFocusedDate] = useState(initialDate);
  const pendingFocus = useRef<string | undefined>(undefined);
  const fallback = initialDate >= from && initialDate <= to ? initialDate : from;
  const activeDate = focusedDate >= from && focusedDate <= to ? focusedDate : fallback;
  useEffect(() => {
    if (!pendingFocus.current) return;
    const target = containerRef.current?.querySelector<HTMLButtonElement>(`[data-calendar-date="${pendingFocus.current}"]`);
    if (target?.getClientRects().length) {
      target.focus();
      pendingFocus.current = undefined;
    }
  }, [activeDate, from, to]);
  const focusDate = (date: string) => {
    setFocusedDate(date);
    pendingFocus.current = date;
    const target = containerRef.current?.querySelector<HTMLButtonElement>(`[data-calendar-date="${date}"]`);
    if (target?.getClientRects().length) {
      target.focus();
      pendingFocus.current = undefined;
    } else onNavigate(date);
  };
  return {
    containerRef,
    focusDate,
    dayProps: (date: string) => ({
      'data-calendar-date': date,
      tabIndex: date === activeDate ? 0 : -1,
      onFocus: () => setFocusedDate(date),
      onKeyDown: (event: KeyboardEvent<HTMLElement>) => {
        if (event.altKey || event.ctrlKey || event.metaKey) return;
        const next = calendarKeyboardDate(date, event.key, event.shiftKey);
        if (!next) return;
        event.preventDefault();
        focusDate(next);
      },
    }),
  };
}
